import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { setAuthTokenGetter } from "./lib/api";
import { DashboardLayout } from "./components/layout/DashboardLayout";

// Route-level code splitting via React.lazy
const Overview = React.lazy(() => import("./pages/Overview").then(m => ({ default: m.Overview })));
const Compliance = React.lazy(() => import("./pages/Compliance").then(m => ({ default: m.Compliance })));
const Grid = React.lazy(() => import("./pages/Grid").then(m => ({ default: m.Grid })));
const Subsidies = React.lazy(() => import("./pages/Subsidies").then(m => ({ default: m.Subsidies })));
const Reports = React.lazy(() => import("./pages/Reports").then(m => ({ default: m.Reports })));
const Settings = React.lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const Pricing = React.lazy(() => import("./pages/Pricing").then(m => ({ default: m.Pricing })));
const Billing = React.lazy(() => import("./pages/Billing").then(m => ({ default: m.Billing })));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfService })));
const Impressum = React.lazy(() => import("./pages/Impressum").then(m => ({ default: m.Impressum })));
const Landing = React.lazy(() => import("./pages/Landing").then(m => ({ default: m.Landing })));
const Audit = React.lazy(() => import("./pages/Audit").then(m => ({ default: m.Audit })));
const ComplianceScore = React.lazy(() => import("./pages/ComplianceScore").then(m => ({ default: m.ComplianceScore })));
const Blog = React.lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const BlogAfirComplianceGuide2026 = React.lazy(() => import("./pages/BlogAfirComplianceGuide2026").then(m => ({ default: m.BlogAfirComplianceGuide2026 })));
const BlogEichrechtKonformitaetChecklist = React.lazy(() => import("./pages/BlogEichrechtKonformitaetChecklist").then(m => ({ default: m.BlogEichrechtKonformitaetChecklist })));
const BlogCpmsBuyerChecklist = React.lazy(() => import("./pages/BlogCpmsBuyerChecklist").then(m => ({ default: m.BlogCpmsBuyerChecklist })));
const FAQ = React.lazy(() => import("./pages/FAQ").then(m => ({ default: m.FAQ })));
const AfirComplianceSoftware = React.lazy(() => import("./pages/GeoCompliancePages").then(m => ({ default: m.AfirComplianceSoftware })));
const EichrechtCompliance = React.lazy(() => import("./pages/GeoCompliancePages").then(m => ({ default: m.EichrechtCompliance })));
const LadeinfrastrukturCompliance = React.lazy(() => import("./pages/GeoCompliancePages").then(m => ({ default: m.LadeinfrastrukturCompliance })));

function AuthenticatedApp() {
  const { getToken } = useAuth();

  // Initialize API client with auth token getter
  useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);

  return (
    <DashboardLayout>
      <Suspense fallback={<div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>}>
        <Routes>
          <Route path="/dashboard" element={<Overview />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/grid" element={<Grid />} />
          <Route path="/subsidies" element={<Subsidies />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/billing/success" element={<Billing />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </DashboardLayout>
  );
}

/** Public routes that don't require authentication */
const PUBLIC_PATHS = [
  "/",
  "/audit",
  "/score",
  "/privacy",
  "/terms",
  "/impressum",
  "/blog",
  "/faq",
  "/pricing",
  "/afir-compliance-software",
  "/eichrecht-compliance",
  "/ladeinfrastruktur-compliance",
];

export function App() {
  const location = useLocation();
  const isPublicPath =
    PUBLIC_PATHS.includes(location.pathname) ||
    location.pathname.startsWith("/blog/");

  if (isPublicPath) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen text-gray-500">Loading...</div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/score" element={<ComplianceScore />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/afir-compliance-guide-2026" element={<BlogAfirComplianceGuide2026 />} />
          <Route path="/blog/eichrecht-konformitaet-checklist" element={<BlogEichrechtKonformitaetChecklist />} />
          <Route path="/blog/cpms-buyer-checklist" element={<BlogCpmsBuyerChecklist />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/afir-compliance-software" element={<AfirComplianceSoftware />} />
          <Route path="/eichrecht-compliance" element={<EichrechtCompliance />} />
          <Route path="/ladeinfrastruktur-compliance" element={<LadeinfrastrukturCompliance />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <SignedIn>
        <AuthenticatedApp />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}
